/**
 * One-tap on a market order: three buttons that each place an order at once —
 * `docs/07` §1c.
 *
 * ```
 *  One-tap buy                    Edit      ← a field label's row
 *  ┌────────┐ ┌────────┐ ┌────────┐
 *  │  $25   │ │  $50   │ │  $100  │         ← where a limit order's Price sits
 *  │win $49 │ │win $98 │ │win $192│
 *  └────────┘ └────────┘ └────────┘
 * ```
 *
 * In the market order's own column, in the slot a limit order gives its Price
 * field: a market order has no price, and the presets are the market order
 * placed by amount. On the sell side the three are 25% / 50% / All of what the
 * account can sell, each with what it returns. `domain/one-tap.ts` is the
 * arithmetic and the argument — a tap never spends more than its amount and
 * never sells more than is held; this renders it, checks each order, and
 * places one on a tap.
 *
 * ## One opinion about the gate
 *
 * Every check here is the ticket's own `OrderGateInput` with a tap's order put
 * into it, so this row and the ticket cannot disagree about the market's state,
 * the socket, the balance or the caps. Two tiers, the pro ladder's split
 * (`use-ladder-trading.ts`):
 *
 * - **The standing gate** — `validateOrderShape` at a legal placeholder size —
 *   says whether anything may be placed at all. Blocked, all three are
 *   disabled with the reason as their tooltip; the ticket's own button below
 *   carries the remedy (`CONNECT WALLET`, `ENABLE TRADING`…) and the helper
 *   line, so this row restates neither.
 * - **Each preset** is `validateOrder` on its own order: a preset the balance,
 *   the book or a cap refuses is disabled with the gate's own sentence, and the
 *   other two stay live. The tap re-checks at the click's own instant.
 *
 * ## One tab stop
 *
 * The row is a `toolbar`: one stop in the ticket's tab order, with the arrows
 * moving between `Edit` and the presets — the radio groups' model
 * (`radio-group.ts`, invariant 20). Four more stops between the outcome and
 * the size field would put a keyboard user further from the order than a
 * mouse user, and `e2e/a11y.spec.ts` holds the walk to the button at six.
 *
 * ## No confirm step, so one tap at a time
 *
 * A tap is the order. While one is being answered every preset waits (the
 * ticket's `inFlight` rule — a ref, because a second click can land before a
 * re-render), and each tap is its own `client_order_id` (invariant 15).
 */

import { memo, useRef, useState } from 'react'
import { Button, Input, Spinner } from '@/components/primitives'
import { nextRadioIndex } from '@/components/primitives/radio-group'
import { useRuntime } from '@/app/use-runtime'
import { useStoreField } from '@/app/use-store'
import { setOneTapAmounts } from '@/app/preferences'
import { announceSelfMatch } from '@/trading/announce'
import { cx } from '@/lib/cx'
import { formatFixed, parseFixed, SHARES_DP, USDC_DP, type Outcome } from '@/lib/decimal'
import { formatShares, formatUsdc } from '@/lib/format'
import { EMPTY_QUOTE } from '@/domain/quote'
import { splitSell } from '@/domain/sell-shape'
import {
  formatOneTapAmount,
  ONE_TAP_SELL_PERCENTS,
  oneTapBuy,
  oneTapSell,
  parseOneTapAmount,
  type OneTapOrder,
} from '@/domain/one-tap'
import {
  isIdentityAction,
  validateOrder,
  validateOrderShape,
  type OrderGate,
  type OrderGateInput,
} from '@/domain/order-validation'
import type { TradingLimits } from '@/domain/order-config'
import type { BookState } from '@/stores/book'
import { pushToast, uiStore } from '@/stores/ui'

export interface OneTapPanelProps {
  marketId: string | null
  side: 'BUY' | 'SELL'
  outcome: Outcome
  book: BookState | null
  /**
   * The book's version. `book` is mutated in place and keeps its identity for
   * the life of a market, so this is what makes the presets look again
   * (invariant 17) — read in the render, never only in a memo's list.
   */
  bookVersion: number
  limits: TradingLimits
  /** Shares of `outcome` the account can sell now — held, less its own resting sells. */
  deliverableShares: bigint
  /** The ticket's own gate input. Every check here is this, with a tap's order in it. */
  gateInput: OrderGateInput
  expiryAtMs: number
  upTokenId: string | null
  downTokenId: string | null
}

interface Preset {
  /** `$25` or `25%` — the button's first line. */
  label: string
  order: OneTapOrder
}

/**
 * What an identity gate means for a preset, as its tooltip. `validateOrder`
 * gives those gates button copy (`CONNECT WALLET`) and no reason; the ticket's
 * button below is the remedy, so this only says why the presets wait.
 */
const IDENTITY_REASON: Record<string, string> = {
  CONNECT: 'Connect your wallet to trade.',
  SIGN_IN: 'Sign in to trade.',
  WRONG_ACCOUNT: 'Your wallet is on a different account. Sign in again to trade.',
  WRONG_CHAIN: 'Your wallet is on another network. Switch it to enable trading.',
  ENABLE_TRADING: 'Enable trading to place orders.',
}

export const OneTapPanel = memo(function OneTapPanel({
  marketId,
  side,
  outcome,
  book,
  bookVersion,
  limits,
  deliverableShares,
  gateInput,
  expiryAtMs,
  upTokenId,
  downTokenId,
}: OneTapPanelProps) {
  const runtime = useRuntime()
  // Joined, because a field subscription compares by value and the tuple is
  // replaced whole when an edit is saved.
  const amounts = useStoreField(uiStore, (s) => s.oneTapAmounts.join(',')).split(',')
  const [editing, setEditing] = useState<string[] | null>(null)
  const [tapping, setTapping] = useState<number | null>(null)
  /** The toolbar stop the keyboard last stood on — see `stops` below. */
  const [active, setActive] = useState<number | null>(null)
  const rowRef = useRef<HTMLDivElement>(null)
  /** The ticket's double-click guard, for the same reason it is a ref there. */
  const inFlight = useRef(false)

  // Read so a book frame re-renders the presets even though `book` itself
  // never changes identity (invariant 17).
  void bookVersion

  /**
   * The standing gate: everything that does not depend on a tap's own order.
   * `validateOrderShape` ends in the size check, so it is asked about a size
   * that passes it — the minimum — and each preset answers for its own size.
   * No funding steps: a short balance is each preset's own balance check.
   */
  const standing = validateOrderShape({
    ...gateInput,
    setup: [],
    orderType: 'LIMIT',
    sizeShares: limits.minOrderShares,
    limitPriceCents: null,
    quote: EMPTY_QUOTE,
    coveredShares: 0n,
  })
  const standingReason =
    standing === null ? null
    : isIdentityAction(standing.code) ? (IDENTITY_REASON[standing.code] ?? standing.label)
    : (standing.helper ?? standing.label)

  const presets: Preset[] =
    side === 'BUY' ?
      amounts.map((stored) => {
        const micro = parseOneTapAmount(stored) ?? 0n
        return { label: amountLabel(micro), order: oneTapBuy(book, outcome, micro, limits) }
      })
    : ONE_TAP_SELL_PERCENTS.map((percent) => ({
        label: percent >= 100 ? 'All' : `${percent}%`,
        order: oneTapSell(book, outcome, deliverableShares, percent, limits),
      }))

  /** One preset's verdict, in the ticket's terms. `nowMs` is the click's at a click. */
  const gateOf = (order: OneTapOrder, nowMs: number): OrderGate => {
    if (order.empty !== null || order.quoteLimitCents === null) {
      return {
        code: order.empty === 'NO_LIQUIDITY' ? 'INSUFFICIENT_LIQUIDITY' : 'SIZE_BELOW_MIN',
        blocked: true,
        label: null,
        helper: emptySentence(order, limits),
      }
    }
    return validateOrder({
      ...gateInput,
      nowMs,
      setup: [],
      orderType: 'LIMIT',
      outcome: order.quoteOutcome,
      sizeShares: order.sizeShares,
      limitPriceCents: order.quoteLimitCents,
      quote: order.quote,
      coveredShares: order.coveredShares,
    })
  }

  const tap = async (index: number) => {
    const order = presets[index]?.order
    if (!order || marketId === null || inFlight.current || standing !== null) return
    // Re-checked at the click's own instant (invariant 7): the market's clock
    // has moved since the render that drew the button.
    const decision = gateOf(order, runtime.clock.now())
    if (decision.blocked) {
      pushToast({ tone: 'warning', title: decision.label ?? 'Not placed', body: decision.helper ?? '' })
      return
    }
    if (order.limitCents === null) return

    // A sell goes through `splitSell` like every sell in this client, and is
    // placed only if it is the single covered sell it was sized to be — never
    // rewritten into a buy of the other side, which a tap cannot explain first.
    const legs =
      order.side === 'BUY' ?
        [{ outcome, side: 'BUY' as const, priceCents: order.limitCents, sizeShares: order.sizeShares, rewritten: false }]
      : splitSell({
          outcome,
          sizeShares: order.sizeShares,
          priceCents: order.limitCents,
          coverableShares: deliverableShares,
          minOrderShares: limits.minOrderShares,
          sizeStep: limits.sizeStep,
        })
    const leg = legs.length === 1 ? legs[0] : undefined
    if (!leg || leg.side !== order.side || leg.rewritten) {
      pushToast({ tone: 'warning', title: 'Not placed', body: 'You no longer hold enough of these shares.' })
      return
    }
    const tokenId = leg.outcome === 'UP' ? upTokenId : downTokenId
    // Inside the digest, so there is no signing without either (docs/06 §F75).
    const expirationGraceMs = limits.orderExpirationGraceMs
    if (tokenId === null || expirationGraceMs === null) return

    inFlight.current = true
    setTapping(index)
    try {
      const result = await runtime.trading.submit({
        marketId,
        outcome: leg.outcome,
        side: leg.side,
        orderType: 'LIMIT',
        // Takes what the book has at or better than the limit, and cancels the
        // rest: nothing a tap places is left resting.
        timeInForce: 'IOC',
        priceCents: leg.priceCents,
        sizeShares: leg.sizeShares,
        postOnly: false,
        reduceOnly: false,
        maxSlippageCents: null,
        marketExpiryAtMs: expiryAtMs,
        expirationGraceMs,
        tokenId: BigInt(tokenId),
        feeRateBps: limits.feeRateBps,
      })
      if (result.status === 'PLACED') {
        announceSelfMatch(result.order)
        announceUnfilled(result.order, order.side)
      }
    } finally {
      inFlight.current = false
      setTapping(null)
    }
  }

  const nowMs = gateInput.nowMs
  const winTone = outcome === 'UP' ? 'text-up-text' : 'text-down-text'
  const heading = side === 'BUY' ? 'One-tap buy' : 'One-tap sell'
  const verdicts = presets.map((preset) => {
    const decision = gateOf(preset.order, nowMs)
    const disabled = standing !== null || decision.blocked
    return { disabled, reason: standing !== null ? standingReason : (decision.helper ?? decision.label) }
  })

  /**
   * The toolbar's stops, in DOM order: `Edit` (buying, and not mid-edit), then
   * the three presets. The roving stop is the one last focused while it is
   * still enabled, else the first live preset — the thing a keyboard user came
   * for — else `Edit`.
   */
  const toolbar = editing === null
  const hasEdit = side === 'BUY' && toolbar
  const stops = [...(hasEdit ? [true] : []), ...verdicts.map((v) => !v.disabled && tapping === null)]
  const firstPreset = stops.findIndex((on, i) => on && (!hasEdit || i > 0))
  const roving =
    active !== null && stops[active] ? active
    : firstPreset >= 0 ? firstPreset
    : hasEdit ? 0
    : null
  const presetStop = (i: number) => (hasEdit ? i + 1 : i)
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!toolbar || roving === null) return
    const to = nextRadioIndex(event.key, roving, stops.length, (i) => stops[i] === true)
    if (to === null) return
    event.preventDefault()
    setActive(to)
    rowRef.current?.querySelector<HTMLElement>(`[data-stop="${to}"]`)?.focus()
  }

  return (
    <div
      ref={rowRef}
      className="flex flex-col gap-1"
      data-testid="one-tap"
      data-gate={standing?.code ?? 'READY'}
      {...(toolbar ? { role: 'toolbar', 'aria-label': heading, onKeyDown } : {})}
    >
      {/* A field label's row, so the presets read as the market order's own field. */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-caption text-text-secondary">{heading}</span>
        {hasEdit ? (
          <button
            type="button"
            data-stop={0}
            tabIndex={roving === 0 ? 0 : -1}
            onFocus={() => setActive(0)}
            onClick={() => setEditing([...amounts])}
            className={cx(
              'inline-flex items-center justify-center text-caption font-semibold text-accent-text underline-offset-2 hover:underline',
              'focus-visible:shadow-focus focus-visible:outline-none',
              // The touch floor below 768px (invariant 20): a 21×16 link is a miss.
              'min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] md:min-h-0 md:min-w-0',
            )}
          >
            Edit
          </button>
        ) : null}
      </div>

      {side === 'BUY' && editing !== null ?
        <AmountEditor
          values={editing}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={(saved) => {
            setOneTapAmounts(saved)
            setEditing(null)
          }}
        />
      : <div className="grid grid-cols-3 gap-1_5">
          {presets.map((preset, i) => {
            const { disabled, reason } = verdicts[i] as { disabled: boolean; reason: string | null }
            const value = preset.order.valueMicroUsdc
            const stop = presetStop(i)
            return (
              <PresetButton
                key={i}
                stop={stop}
                tabbable={roving === stop}
                onFocus={() => setActive(stop)}
                label={preset.label}
                detail={`${side === 'BUY' ? 'win' : 'get'} ${value > 0n ? formatUsdc(formatFixed(value, USDC_DP)) : '—'}`}
                detailClassName={side === 'BUY' ? winTone : 'text-text-secondary'}
                disabled={disabled || tapping !== null}
                disabledReason={disabled ? reason : null}
                loading={tapping === i}
                onTap={() => void tap(i)}
              />
            )
          })}
        </div>
      }
    </div>
  )
})

// ---------------------------------------------------------------------------

/**
 * One preset. Its own `<button>` rather than `<Button>`, because it is two
 * lines — the amount and what it wins — in a field's height, and `<Button>` is
 * a one-line control. It keeps `<Button>`'s two rules: loading keeps the
 * width, and disabled always states its reason — as the tooltip and the
 * accessible *description*, never inside the name: a name of
 * `$25 win $49.01 Enable trading` is a second button called "Enable trading"
 * to anyone finding the ticket's own by name.
 */
function PresetButton({
  stop,
  tabbable,
  onFocus,
  label,
  detail,
  detailClassName,
  disabled,
  disabledReason,
  loading,
  onTap,
}: {
  stop: number
  tabbable: boolean
  onFocus: () => void
  label: string
  detail: string
  detailClassName: string
  disabled: boolean
  disabledReason: string | null
  loading: boolean
  onTap: () => void
}) {
  return (
    <button
      type="button"
      data-testid="one-tap-preset"
      data-stop={stop}
      tabIndex={tabbable ? 0 : -1}
      onFocus={onFocus}
      disabled={disabled}
      aria-busy={loading || undefined}
      title={disabled && disabledReason ? disabledReason : undefined}
      onClick={onTap}
      className={cx(
        // The Price field's height, and the touch floor below 768px, where the
        // control-height tokens rise to it (invariant 20).
        'relative flex h-[var(--ud-control-height-lg)] flex-col items-center justify-center rounded-md border px-1 leading-tight',
        'transition-[background-color,border-color] duration-[var(--ud-duration-instant)] ease-out',
        'focus-visible:shadow-focus focus-visible:outline-none',
        disabled && !loading ?
          'cursor-not-allowed border-transparent bg-bg-active text-text-disabled'
        : 'border-border-default bg-bg-raised text-text-primary hover:border-border-strong hover:bg-bg-hover active:bg-bg-active',
      )}
    >
      <span className={cx('ud-num text-body-sm font-semibold', loading && 'invisible')}>{label}</span>
      <span
        className={cx('ud-num text-micro', loading && 'invisible', disabled && !loading ? 'text-text-disabled' : detailClassName)}
      >
        {detail}
      </span>
      {loading ? (
        <span className="absolute inset-0 inline-flex items-center justify-center">
          <Spinner />
        </span>
      ) : null}
    </button>
  )
}

/** The three amounts as inputs, saved together or not at all. */
function AmountEditor({
  values,
  onChange,
  onCancel,
  onSave,
}: {
  values: string[]
  onChange: (next: string[]) => void
  onCancel: () => void
  onSave: (saved: [string, string, string]) => void
}) {
  const [checked, setChecked] = useState(false)
  const parsed = values.map(parseOneTapAmount)
  const save = () => {
    setChecked(true)
    const [a, b, c] = parsed
    if (a == null || b == null || c == null) return
    onSave([formatOneTapAmount(a), formatOneTapAmount(b), formatOneTapAmount(c)])
  }
  return (
    <form
      className="flex flex-col gap-1_5"
      data-testid="one-tap-editor"
      onSubmit={(event) => {
        event.preventDefault()
        save()
      }}
    >
      <div className="grid grid-cols-3 gap-1_5">
        {values.map((value, i) => (
          <Input
            key={i}
            aria-label={`Amount ${i + 1}`}
            value={value}
            onChange={(event) => onChange(values.map((v, j) => (j === i ? event.target.value : v)))}
            inputMode="decimal"
            unit="$"
            numeric
            {...(checked && parsed[i] == null ? { error: 'Enter dollars, like 25 or 2.50.' } : {})}
          />
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="secondary" size="sm" type="submit">
          Done
        </Button>
      </div>
    </form>
  )
}

/**
 * Say so when a tap filled nothing: an `IOC` the book moved away from before it
 * arrived is cancelled whole (`IOC_UNFILLED`), and with no fill there is no
 * `Filled` toast — so without this a tap would look like it did nothing.
 */
function announceUnfilled(order: unknown, side: 'BUY' | 'SELL'): void {
  const row = order as Record<string, unknown> | null | undefined
  if (row?.reason !== 'IOC_UNFILLED') return
  if (typeof row.filled_size === 'string' && sharesOf(row.filled_size) > 0n) return
  pushToast({
    tone: 'info',
    title: 'Nothing filled',
    body:
      side === 'BUY' ?
        'The price moved before your order reached the book, so nothing was bought. Tap again to buy at the new price.'
      : 'The price moved before your order reached the book, so nothing was sold. Tap again to sell at the new price.',
  })
}

/** Why a preset places nothing, for the cases `validateOrder` has no sentence for. */
function emptySentence(order: OneTapOrder, limits: TradingLimits): string {
  switch (order.empty) {
    case 'NOTHING_HELD':
      return `You hold no ${order.outcome} shares in this market.`
    case 'NO_LIQUIDITY':
      return order.side === 'BUY' ?
          `Nobody is selling ${order.outcome} right now.`
        : `Nobody is buying ${order.outcome} right now.`
    default:
      return `Minimum order is ${sharesText(limits.minOrderShares)} shares.`
  }
}

/** `$25`, or `$2.50` when the amount has cents. */
function amountLabel(micro: bigint): string {
  const value = formatFixed(micro, USDC_DP)
  return micro % 1_000_000n === 0n ? formatUsdc(value, { dp: 0 }) : formatUsdc(value)
}

function sharesText(micro: bigint): string {
  return formatShares(formatFixed(micro, SHARES_DP))
}

function sharesOf(value: string): bigint {
  try {
    return parseFixed(value, SHARES_DP)
  } catch {
    return 0n
  }
}
